import type { CascadeMetrics } from '@/types/cascade-report';
import { decimal, fillTone, money, num, pct } from '../format';
import { MetricHead } from './MetricHead';

interface Props<T extends CascadeMetrics> {
    rows: T[];
    /** Rótulo da primeira coluna. */
    label: string;
    /** Texto da primeira célula. */
    name: (row: T) => string;
    onRowClick?: (row: T) => void;
    empty?: string;
}

/**
 * Tabela genérica das métricas por recorte — bloco, país ou domínio.
 *
 * As três mostram exatamente as mesmas colunas; o que muda é a primeira. Manter
 * três tabelas quase iguais só criaria três lugares para divergirem.
 */
export function MetricTable<T extends CascadeMetrics>({
    rows,
    label,
    name,
    onRowClick,
    empty = 'Nada neste recorte.',
}: Props<T>) {
    if (!rows.length) {
        return <p className="py-8 text-center text-sm text-gray-500">{empty}</p>;
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                        <th className="px-4 py-2 text-left font-medium">{label}</th>
                        <MetricHead metric="cascades" />
                        <MetricHead metric="wins" />
                        <MetricHead metric="fill_rate" label="Preench." />
                        <MetricHead metric="requests_per_sale" />
                        <MetricHead metric="ecpm_avg" />
                        <MetricHead metric="revenue_est" label="Receita est." />
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {rows.map((row, index) => (
                        <tr
                            key={`${name(row)}-${index}`}
                            onClick={() => onRowClick?.(row)}
                            className={
                                onRowClick ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'
                            }
                        >
                            <td className="px-4 py-2 font-medium text-gray-900">{name(row)}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                                {row.cascades_reliable ? num(row.cascades) : '—'}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                                {num(row.wins)}
                            </td>
                            <td
                                className={`px-4 py-2 text-right tabular-nums ${
                                    row.cascades_reliable ? fillTone(row.fill_rate) : 'text-gray-400'
                                }`}
                            >
                                {row.cascades_reliable ? pct(row.fill_rate) : '—'}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                                {row.wins ? decimal(row.requests_per_sale) : '—'}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                                {money(row.ecpm_avg)}
                            </td>
                            <td className="px-4 py-2 text-right font-medium tabular-nums text-gray-900">
                                {money(row.revenue_est)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
